import { StandardEvents } from '@shopify/events';

/**
 * Horizon overrides for Shopify.actions:
 * - updateCart: emit events from the cart drawer scope.
 * - openCart: open the cart drawer (fall back to /cart when absent).
 * - product add: wait for Horizon's success animations before opening the drawer.
 */

const CART_OPEN_SETTLE_DELAY = 120;
let delayedCartOpenToken = 0;

const nextFrame = () => new Promise((resolve) => requestAnimationFrame(resolve));
const delay = (duration) => new Promise((resolve) => setTimeout(resolve, duration));

/**
 * Waits for the button confirmation and fly-to-cart animations that are still active.
 * @param {Event} event
 */
async function waitForAddToCartAnimations(event) {
  await nextFrame();

  const source = event.target instanceof Element ? event.target : null;
  const animatedElements = [
    ...document.querySelectorAll('fly-to-cart'),
    ...(source ? source.querySelectorAll('[data-added="true"]') : []),
  ];
  const animations = animatedElements.flatMap((element) => element.getAnimations({ subtree: true }));

  await Promise.allSettled(animations.map((animation) => animation.finished));
  await delay(CART_OPEN_SETTLE_DELAY);
}

/** @param {import('@shopify/events').CartLinesUpdateEvent} event */
function handleSuccessfulProductAdd(event) {
  if (event.action !== 'add' || event.context !== 'product' || !event.promise) return;

  const token = ++delayedCartOpenToken;
  const sourceModal =
    event.target instanceof Element ? event.target.closest('dialog:modal') : null;

  event.promise
    .then(async ({ detail }) => {
      if (detail?.didError) return;

      await waitForAddToCartAnimations(event);
      if (token !== delayedCartOpenToken) return;

      const openDrawer = () => {
        /** @type {HTMLElement & {open?: () => void, isOpen?: boolean} | null} */
        const drawer = document.querySelector('theme-drawer#cart-drawer');
        if (!drawer?.isOpen) drawer?.open?.();
      };

      if (sourceModal?.open) {
        sourceModal.addEventListener('close', openDrawer, { once: true });
      } else {
        openDrawer();
      }
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[komiari-cart] Delayed open failed:', error);
    });
}

document.addEventListener(StandardEvents.cartLinesUpdate, handleSuccessfulProductAdd);

function init() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.updateCart.configure({
    eventTarget: () => document.querySelector('theme-drawer#cart-drawer') ?? document,
  });

  actions.openCart.configure({
    async handler() {
      /** @type {HTMLElement & {open?: () => void} | null} */
      const drawer = document.querySelector('theme-drawer#cart-drawer');

      if (drawer?.open) {
        drawer.open();
      } else {
        window.location.href = Theme.routes.cart_url || '/cart';
      }
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init, { once: true });
}
